import { BaseService } from "../api/BaseService";
import type {
  CreateEventRequest,
  EventResponse,
  UpdateEventRequest,
  EventFilter,
  ListEventResponse,
  ListEventsOfFollowingRequest,
  ListEventsOfFollowingResponse,
  CreateEventServiceRequest,
  EventServiceResponse,
  UpdateEventServiceRequest,
  EventServiceFilter,
  ListEventServiceResponse,
  GetEventServicesByTicketIDResponse,
  CreateGuestRequest,
  GuestResponse,
  UpdateGuestRequest,
  GuestFilter,
  ListGuestResponse,
  CreateTicketRequest,
  TicketResponse,
  UpdateTicketRequest,
  TicketFilter,
  ListTicketResponse,
  ListTicketByGuestEmailResponse,
  CreateTicketTypeRequest,
  TicketTypeResponse,
  UpdateTicketTypeRequest,
  TicketTypeFilter,
  ListTicketTypeResponse,
  EventAddImageResponse,
  AddImageToEventAdapterRequest,
  ListImageEventResponse,
  CreateConfirmationRequest,
  CreateConfirmationResponse,
  GetConfirmationRequestResponse,
  PatchConfirmationRequestByUserRequest,
  PatchConfirmationRequestByUserResponse,
  PatchConfirmationRequestByEstablishmentRequest,
  PatchConfirmationRequestByEstablishmentResponse,
  GetConfirmationRequestsForCurrentUserResponse,
  GetConfirmationRequestsByEstablishmentResponse,
  GetConfirmationRequestsByUserAndEstablishmentResponse,
  GetConfirmationOverridesByEstablishmentResponse,
  GetConfirmationRequestsByEventAndEstablishment,
  EventsImageUploadRequest,
  CreateEventsCategoryRequest,
  UpdateEventsCategoryRequest,
  EventsCategoryResponse,
  ListEventsCategoryResponse,
  EventsCategoryFilter,
  ListEventAnnouncementsResponse,
  EventAnnouncementResponse,
  ListNewsletterSubscribersResponse,
  RemoveNewsletterSubscriberResponse,
} from "../models/events";
import type { FinalResponse } from "../models";

export class EventsService extends BaseService {
  private static readonly SERVICE = "Event";

  // Category methods
  async createCategory(
    data: CreateEventsCategoryRequest,
  ): Promise<EventsCategoryResponse> {
    const res = await this.api.request<FinalResponse<EventsCategoryResponse>>(
      "/event/category",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async updateCategory(
    id: string,
    data: UpdateEventsCategoryRequest,
  ): Promise<EventsCategoryResponse> {
    const res = await this.api.request<FinalResponse<EventsCategoryResponse>>(
      `/event/category/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async deleteCategory(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/event/category/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EventsService.SERVICE);
  }

  async fetchCategory(id: string): Promise<EventsCategoryResponse> {
    const res = await this.api.request<FinalResponse<EventsCategoryResponse>>(
      `/event/category/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listCategories(
    page: number = 1,
    limit: number = 10,
    filter?: EventsCategoryFilter,
  ): Promise<ListEventsCategoryResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.name) params.append("name", filter.name);

    const res = await this.api.request<
      FinalResponse<ListEventsCategoryResponse>
    >(`/event/category?${params}`, {
      method: "GET",
    });
    return this.unwrap(res, EventsService.SERVICE);
  }

  // Event methods
  async createEvent(data: CreateEventRequest): Promise<EventResponse> {
    const res = await this.api.request<FinalResponse<EventResponse>>(
      "/event/event",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async updateEvent(data: UpdateEventRequest): Promise<EventResponse> {
    const res = await this.api.request<FinalResponse<EventResponse>>(
      `/event/event/${data.id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async deleteEvent(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/event/event/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EventsService.SERVICE);
  }

  async fetchEvent(id: string): Promise<EventResponse> {
    const res = await this.api.request<FinalResponse<EventResponse>>(
      `/event/event/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrapDetail(res, EventsService.SERVICE);
  }

  async fetchEventByToken(id: string, token: string): Promise<EventResponse> {
    const res = await this.api.request<FinalResponse<EventResponse>>(
      `/event/event/${id}?token=${encodeURIComponent(token)}`,
      {
        method: "GET",
      },
    );
    return this.unwrapDetail(res, EventsService.SERVICE);
  }

  async listEvents(
    page: number = 1,
    limit: number = 10,
    filter?: EventFilter,
  ): Promise<ListEventResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.name) params.append("name", filter.name);
    if (filter?.establishmentId)
      params.append("establishment_id", filter.establishmentId);
    if (filter?.categoryId)
      filter.categoryId.forEach((id) => params.append("category_id", id));
    if (filter?.startDate) params.append("start_date", filter.startDate);
    // Support optional start_time (some backends require both date + time)
    if (filter?.startTime) params.append("start_time", filter.startTime);
    if (filter?.endDate) params.append("end_date", filter.endDate);
    if (filter?.endTime) params.append("end_time", filter.endTime);
    if (filter?.latitude !== undefined)
      params.append("latitude", filter.latitude.toString());
    if (filter?.longitude !== undefined)
      params.append("longitude", filter.longitude.toString());
    if (filter?.radius !== undefined)
      params.append("radius", filter.radius.toString());
    if (filter?.userTimeZone)
      params.append("user_time_zone", filter.userTimeZone);

    const res = await this.api.request<FinalResponse<ListEventResponse>>(
      `/event/event?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listNewsletterSubscribers(
    establishmentId: string,
  ): Promise<ListNewsletterSubscribersResponse> {
    const params = new URLSearchParams({ establishmentId });
    const res = await this.api.request<
      FinalResponse<ListNewsletterSubscribersResponse>
    >(`/event/newsletter/subscribers?${params}`, { method: "GET" });
    const data = this.unwrap(res, EventsService.SERVICE, {
      missing: "The subscriber service response is missing.",
      failed: (status) => `Subscriber request failed with status ${status}.`,
    });
    if (!Array.isArray(data?.subscribers)) {
      throw new Error("The subscriber service returned an invalid response.");
    }

    return data;
  }

  async removeNewsletterSubscriber(
    establishmentId: string,
    subscriberId: string,
  ): Promise<RemoveNewsletterSubscriberResponse> {
    const params = new URLSearchParams({ establishmentId });
    const res = await this.api.request<
      FinalResponse<RemoveNewsletterSubscriberResponse>
    >(
      `/event/newsletter/subscribers/${encodeURIComponent(subscriberId)}?${params}`,
      { method: "DELETE" },
      0,
    );
    const data = this.unwrap(res, EventsService.SERVICE, {
      missing: "The subscriber service response is missing.",
      failed: (status) => `Subscriber removal failed with status ${status}.`,
    });
    if (data?.status !== "removed") {
      throw new Error(
        "The subscriber service returned an invalid removal response.",
      );
    }

    return data;
  }

  async listEventsOfFollowing(
    data: ListEventsOfFollowingRequest,
  ): Promise<ListEventsOfFollowingResponse> {
    const res = await this.api.request<
      FinalResponse<ListEventsOfFollowingResponse>
    >("/event/event/following", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return this.unwrap(res, EventsService.SERVICE);
  }

  // EventService methods
  async createEventService(
    data: CreateEventServiceRequest,
  ): Promise<EventServiceResponse> {
    const res = await this.api.request<FinalResponse<EventServiceResponse>>(
      "/event/service",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async updateEventService(
    id: string,
    data: UpdateEventServiceRequest,
  ): Promise<EventServiceResponse> {
    const res = await this.api.request<FinalResponse<EventServiceResponse>>(
      `/event/service/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async deleteEventService(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/event/service/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EventsService.SERVICE);
  }

  async fetchEventService(
    id: string,
    page: string,
    limit: string,
  ): Promise<EventServiceResponse> {
    const params = new URLSearchParams({
      page: page,
      limit: limit,
      event_id: id,
    });

    const res = await this.api.request<FinalResponse<EventServiceResponse>>(
      `/event/service?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listEventServices(
    page: number = 1,
    limit: number = 10,
    filter?: EventServiceFilter,
  ): Promise<ListEventServiceResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.eventId) params.append("event_id", filter.eventId);
    if (filter?.serviceId) params.append("service_id", filter.serviceId);

    const res = await this.api.request<FinalResponse<ListEventServiceResponse>>(
      `/event/service?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async getEventServicesByTicketID(
    ticketId: string,
  ): Promise<GetEventServicesByTicketIDResponse> {
    const res = await this.api.request<
      FinalResponse<GetEventServicesByTicketIDResponse>
    >(`/event/ticket/${ticketId}/services`, {
      method: "GET",
    });
    return this.unwrap(res, EventsService.SERVICE);
  }

  // Guest methods
  async createGuest(data: CreateGuestRequest): Promise<GuestResponse> {
    const res = await this.api.request<FinalResponse<GuestResponse>>(
      "/event/guest",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async updateGuest(
    id: string,
    data: UpdateGuestRequest,
  ): Promise<GuestResponse> {
    const res = await this.api.request<FinalResponse<GuestResponse>>(
      `/event/guest/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async deleteGuest(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/event/guest/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EventsService.SERVICE);
  }

  async fetchGuest(id: string): Promise<GuestResponse> {
    const res = await this.api.request<FinalResponse<GuestResponse>>(
      `/event/guest/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listGuests(
    page: number = 1,
    limit: number = 10,
    filter?: GuestFilter,
  ): Promise<ListGuestResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.eventId) params.append("event_id", filter.eventId);
    if (filter?.userId) params.append("user_id", filter.userId);

    const res = await this.api.request<FinalResponse<ListGuestResponse>>(
      `/event/guest?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  // Ticket methods
  async createTicket(data: CreateTicketRequest): Promise<TicketResponse> {
    const res = await this.api.request<FinalResponse<TicketResponse>>(
      "/event/ticket",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async updateTicket(
    id: string,
    data: UpdateTicketRequest,
  ): Promise<TicketResponse> {
    const res = await this.api.request<FinalResponse<TicketResponse>>(
      `/event/ticket/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async deleteTicket(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/event/ticket/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EventsService.SERVICE);
  }

  async fetchTicket(id: string): Promise<TicketResponse> {
    const res = await this.api.request<FinalResponse<TicketResponse>>(
      `/event/ticket/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listTickets(
    page: number = 1,
    limit: number = 10,
    filter?: TicketFilter,
  ): Promise<ListTicketResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.eventId) params.append("event_id", filter.eventId);
    if (filter?.userId) params.append("user_id", filter.userId);
    if (filter?.guestId) params.append("guest_id", filter.guestId);

    const res = await this.api.request<FinalResponse<ListTicketResponse>>(
      `/event/ticket?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listTicketsByGuestEmail(
    email: string,
    eventId?: string,
  ): Promise<ListTicketByGuestEmailResponse> {
    const params = new URLSearchParams({ email });
    if (eventId) params.append("event_id", eventId);

    const res = await this.api.request<
      FinalResponse<ListTicketByGuestEmailResponse>
    >(`/event/internal/ticket/by-guest-email?${params}`, { method: "GET" });
    return this.unwrap(res, EventsService.SERVICE);
  }

  // TicketType methods
  async createTicketType(
    data: CreateTicketTypeRequest,
  ): Promise<TicketTypeResponse> {
    const res = await this.api.request<FinalResponse<TicketTypeResponse>>(
      "/event/ticket-type",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async updateTicketType(
    id: string,
    data: UpdateTicketTypeRequest,
  ): Promise<TicketTypeResponse> {
    const res = await this.api.request<FinalResponse<TicketTypeResponse>>(
      `/event/ticket-type/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async deleteTicketType(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/event/ticket-type/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EventsService.SERVICE);
  }

  async fetchTicketType(id: string): Promise<TicketTypeResponse> {
    const res = await this.api.request<FinalResponse<TicketTypeResponse>>(
      `/event/ticket-type/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listTicketTypes(
    page: number = 1,
    limit: number = 10,
    filter?: TicketTypeFilter,
  ): Promise<ListTicketTypeResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.eventId) params.append("event_id", filter.eventId);
    if (filter?.name) params.append("name", filter.name);

    const res = await this.api.request<FinalResponse<ListTicketTypeResponse>>(
      `/event/ticket-type?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  // Service to Ticket methods
  async addServiceToTicket(serviceId: string, ticketId: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/event/ticket/${ticketId}/service/${serviceId}`,
      {
        method: "POST",
      },
    );
    this.unwrap(res, EventsService.SERVICE);
  }

  async removeServiceFromTicket(
    serviceId: string,
    ticketId: string,
  ): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/event/ticket/${ticketId}/service/${serviceId}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EventsService.SERVICE);
  }

  // Image methods
  async addImageToEvent(
    request: EventsImageUploadRequest,
    eventId: string,
  ): Promise<EventAddImageResponse> {
    const formData = new FormData();
    formData.append("image", request.image);
    request.tags.forEach((tag) => formData.append("tags", tag));
    if (request.altText) formData.append("altText", request.altText);
    if (request.caption) formData.append("caption", request.caption);
    const res = await this.api.request<FinalResponse<EventAddImageResponse>>(
      `/event/event/${eventId}/image`,
      {
        method: "POST",
        body: formData,
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async addImageToEventAdapter(
    request: AddImageToEventAdapterRequest,
  ): Promise<EventAddImageResponse> {
    const res = await this.api.request<FinalResponse<EventAddImageResponse>>(
      "/event/event/image",
      {
        method: "POST",
        body: JSON.stringify(request),
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async removeImageFromEvent(imageId: string, eventId: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/event/event/${eventId}/image/${imageId}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EventsService.SERVICE);
  }

  async listImagesForEvent(eventId: string): Promise<ListImageEventResponse> {
    const res = await this.api.request<FinalResponse<ListImageEventResponse>>(
      `/event/event/${eventId}/image`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  // Confirmation methods
  async createConfirmationRequest(
    data: CreateConfirmationRequest,
  ): Promise<CreateConfirmationResponse> {
    const res = await this.api.request<
      FinalResponse<CreateConfirmationResponse>
    >("/event/confirmation-request", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return this.unwrap(res, EventsService.SERVICE);
  }

  async getConfirmationRequest(
    confirmationRequestId: string,
  ): Promise<GetConfirmationRequestResponse> {
    const res = await this.api.request<
      FinalResponse<GetConfirmationRequestResponse>
    >(`/event/confirmation-request/${confirmationRequestId}`, {
      method: "GET",
    });
    return this.unwrap(res, EventsService.SERVICE);
  }

  async patchConfirmationRequestByUser(
    confirmationRequestId: string,
    data: PatchConfirmationRequestByUserRequest,
  ): Promise<PatchConfirmationRequestByUserResponse> {
    const res = await this.api.request<
      FinalResponse<PatchConfirmationRequestByUserResponse>
    >(`/event/confirmation-request/${confirmationRequestId}/user`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return this.unwrap(res, EventsService.SERVICE);
  }

  async patchConfirmationRequestByEstablishment(
    confirmationRequestId: string,
    data: PatchConfirmationRequestByEstablishmentRequest,
  ): Promise<PatchConfirmationRequestByEstablishmentResponse> {
    const res = await this.api.request<
      FinalResponse<PatchConfirmationRequestByEstablishmentResponse>
    >(`/event/confirmation-request/${confirmationRequestId}/establishment`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listConfirmationRequestsForCurrentUser(): Promise<GetConfirmationRequestsForCurrentUserResponse> {
    const res = await this.api.request<
      FinalResponse<GetConfirmationRequestsForCurrentUserResponse>
    >("/event/confirmation-request", {
      method: "GET",
    });
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listConfirmationRequestsByEstablishment(
    establishmentId: string,
  ): Promise<GetConfirmationRequestsByEstablishmentResponse> {
    const res = await this.api.request<
      FinalResponse<GetConfirmationRequestsByEstablishmentResponse>
    >(`/event/confirmation-request/establishment/${establishmentId}`, {
      method: "GET",
    });
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listConfirmationRequestsByUserAndEstablishment(
    establishmentId: string,
    userId: string,
  ): Promise<GetConfirmationRequestsByUserAndEstablishmentResponse> {
    const res = await this.api.request<
      FinalResponse<GetConfirmationRequestsByUserAndEstablishmentResponse>
    >(
      `/event/confirmation-request/establishment/${establishmentId}/user/${userId}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listConfirmationOverridesByEstablishment(
    establishmentId: string,
  ): Promise<GetConfirmationOverridesByEstablishmentResponse> {
    const res = await this.api.request<
      FinalResponse<GetConfirmationOverridesByEstablishmentResponse>
    >(
      `/event/confirmation-request/establishment/${establishmentId}/overrides`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  async listConfirmationRequestsByEventAndEstablishment(
    eventId: string,
    establishmentId: string,
  ): Promise<GetConfirmationRequestsByEventAndEstablishment> {
    const res = await this.api.request<
      FinalResponse<GetConfirmationRequestsByEventAndEstablishment>
    >(
      `/event/confirmation-request/event/${eventId}/establishment/${establishmentId}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EventsService.SERVICE);
  }

  // Announcement methods
  async listEventAnnouncements(
    eventId: string,
  ): Promise<ListEventAnnouncementsResponse> {
    const res = await this.api.request<
      FinalResponse<ListEventAnnouncementsResponse>
    >(`/event/event/${eventId}/announcement`, {
      method: "GET",
    });
    return this.unwrap(res, EventsService.SERVICE);
  }

  async getEventAnnouncement(
    eventId: string,
    announcementId: string,
  ): Promise<EventAnnouncementResponse> {
    const res = await this.api.request<
      FinalResponse<EventAnnouncementResponse>
    >(`/event/event/${eventId}/announcement/${announcementId}`, {
      method: "GET",
    });
    return this.unwrap(res, EventsService.SERVICE);
  }
}
